import React from 'react';
import { MapPin, Clock, Info, ShoppingBag } from 'lucide-react';
import { Restaurant, RestaurantSettings } from '../../lib/types';
import { Badge } from '../ui/Badge';
import { formatCurrency } from '../../lib/utils';
interface RestaurantHeaderProps {
  restaurant: Restaurant;
  settings: RestaurantSettings | null;
  zoneName?: string;
}
export function RestaurantHeader({
  restaurant,
  settings,
  zoneName
}: RestaurantHeaderProps) {
  const primaryColor = restaurant.primary_color || '#111827';
  return <div className="bg-white border-b border-gray-100" data-id="element-383">
      {/* Cover Image */}
      <div className="relative h-48 sm:h-64 md:h-80 w-full bg-gray-200" data-id="element-384">
        {restaurant.cover_url ? <img src={restaurant.cover_url} alt={restaurant.name} className="w-full h-full object-cover" data-id="element-385" /> : <div className="w-full h-full" style={{
        backgroundColor: primaryColor,
        opacity: 0.1
      }} data-id="element-386" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" data-id="element-387" />

        {/* Badges */}
        <div className="absolute top-4 right-4 flex flex-col gap-2 items-end" data-id="element-388">
          {restaurant.is_featured && <Badge variant="warning" className="bg-brand-amber text-white border-none shadow-md" data-id="element-389">
              Destacado
            </Badge>}
          {!restaurant.is_open && <Badge variant="danger" className="shadow-md" data-id="element-390">
              Cerrado temporalmente
            </Badge>}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-391">
        <div className="flex flex-col sm:flex-row gap-6 sm:items-end -mt-12 sm:-mt-16 mb-6" data-id="element-392">
          {/* Logo */}
          <div className="relative w-24 h-24 sm:w-32 sm:h-32 rounded-2xl bg-white p-1.5 shadow-xl border border-gray-100 shrink-0" data-id="element-393">
            {restaurant.logo_url ? <img src={restaurant.logo_url} alt={`${restaurant.name} logo`} className="w-full h-full rounded-xl object-cover" data-id="element-394" /> : <div className="w-full h-full rounded-xl flex items-center justify-center text-white font-bold text-4xl" style={{
            backgroundColor: primaryColor
          }} data-id="element-395">
                {restaurant.name.charAt(0)}
              </div>}
          </div>

          {/* Title & Info */}
          <div className="flex-1 pb-2" data-id="element-396">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2 tracking-tight" data-id="element-397">
              {restaurant.name}
            </h1>
            {restaurant.description_short && <p className="text-gray-600 text-sm sm:text-base max-w-2xl mb-4" data-id="element-398">
                {restaurant.description_short}
              </p>}

            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 font-medium" data-id="element-399">
              {(zoneName || restaurant.address_text) && <div className="flex items-center gap-1.5" data-id="element-400">
                  <MapPin className="w-4 h-4" data-id="element-401" />
                  {zoneName || restaurant.address_text}
                </div>}

              {settings?.average_prep_time_minutes && <div className="flex items-center gap-1.5" data-id="element-402">
                  <Clock className="w-4 h-4" data-id="element-403" />
                  {settings.average_prep_time_minutes} min
                </div>}

              {settings?.minimum_order_amount != null && settings.minimum_order_amount > 0 && <div className="flex items-center gap-1.5" data-id="element-404">
                    <Info className="w-4 h-4" data-id="element-405" />
                    Pedido mínimo{' '}
                    {formatCurrency(settings.minimum_order_amount)}
                  </div>}
            </div>
          </div>
        </div>

        {/* Order Methods */}
        {settings && <div className="flex flex-wrap gap-2 pb-6" data-id="element-406">
            {settings.accept_delivery && <Badge variant="info" className="bg-blue-50 text-blue-700 border border-blue-100" data-id="element-407">
                <ShoppingBag className="w-3 h-3 mr-1" data-id="element-408" /> Delivery
              </Badge>}
            {settings.accept_pickup && <Badge variant="success" className="bg-emerald-50 text-emerald-700 border border-emerald-100" data-id="element-409">
                <ShoppingBag className="w-3 h-3 mr-1" data-id="element-410" /> Para llevar
              </Badge>}
          </div>}
      </div>
    </div>;
}